/**
 * Shared reconciliation progress helpers (DTEs, bank transactions).
 * Tier colors: complete ≥100% green, 30–99% amber, <30% salmon.
 */

const CLP_RECONCILE_TOLERANCE = 0.5;

/** Saldo pendiente distinto de cero (tolerancia mínima por float). */
const RECONCILE_REMAINING_EPSILON = 1e-6;

/** True when `remaining` indicates an open balance (source of truth for partial reconciliation). */
export function hasPendingReconciliation(remaining) {
  if (remaining == null || remaining === undefined) return false;
  const rem = Number(remaining);
  if (Number.isNaN(rem)) return false;
  return Math.abs(rem) > RECONCILE_REMAINING_EPSILON;
}

/** @returns {number} Sum of matched amounts from transaction.matchs (always ≥ 0). */
export function sumMatchedAmountFromMatchs(matchs) {
  if (!Array.isArray(matchs) || matchs.length === 0) return 0;
  return matchs.reduce((acc, m) => {
    const v = Number(m?.originalAmount ?? m?.matchedAmount ?? m?.localValue ?? 0) || 0;
    return acc + Math.abs(v);
  }, 0);
}

/**
 * Reconciled amount for display: prefer sum of matchs when present (source of truth),
 * otherwise |amount| − |remaining| (handles negative bank amounts).
 */
export function getReconciledAmount(amount, remaining, matchs) {
  const total = Math.abs(Number(amount ?? 0));
  const fromMatchs = sumMatchedAmountFromMatchs(matchs);
  if (fromMatchs > 0) {
    return Math.min(total, fromMatchs);
  }
  const rem = Math.abs(Number(remaining ?? amount ?? 0));
  return Math.max(0, total - rem);
}

/** True when transaction/document has no pending balance (tolerates CLP rounding). */
export function isFullyReconciled(amount, remaining, matchs) {
  if (hasPendingReconciliation(remaining)) {
    return false;
  }
  const total = Math.abs(Number(amount ?? 0));
  if (total === 0) {
    return Math.abs(Number(remaining ?? 0)) <= CLP_RECONCILE_TOLERANCE;
  }
  const reconciled = getReconciledAmount(amount, remaining, matchs);
  return reconciled >= total - CLP_RECONCILE_TOLERANCE;
}

/** @returns {number} 0–100 */
export function getReconciliationPercent(amount, remaining) {
  const total = Math.abs(amount);
  const rem = Math.abs(remaining);
  if (total === 0) {
    return rem === 0 ? 100 : 0;
  }
  const matched = total - rem;
  const pct = (matched / total) * 100;
  if (Number.isNaN(pct)) return 0;
  return Math.min(100, Math.max(0, pct));
}

/** @returns {'complete'|'progress'|'attention'} */
export function getProgressTier(percent) {
  if (percent >= 100) return "complete";
  if (percent >= 30) return "progress";
  return "attention";
}

export function progressTierClassName(percent) {
  const tier = getProgressTier(percent);
  return `scfo-progress-tier scfo-progress-tier--${tier}`;
}
